/**
 * AtlasVolumetricLightModule — the SHADOWED light volume around the player
 * (shader additions wave, 2026-09-15): sun shafts between buildings and
 * through the canopy, the street lamps' cones at night, headlights in dust.
 *
 * One box (`VOLUME_SIZE_M`) follows the player in 8 m steps; its material
 * (engine `createShadowedLightVolume`) ray-marches every pixel the box covers
 * and evaluates the scene's lights WITH their shadow maps at each step — the
 * sun through the CSM, the lamp pool as point/spot lights — under an HG phase.
 * Nothing else in the frame changes: the volume is a transparent mesh drawn
 * after the opaques, attenuating what is behind it by its own transmittance.
 *
 * What drives it: the atmosphere's fog density (the same FogExp2 the sky
 * writes) and the weather set the extinction (`volumeDensityFor`); the night
 * factor thins the ambient floor so fog never glows in the dark; the weather's
 * wind drifts the noise; the tier picks the march steps. Off in space and
 * while the flag is off — the mesh is removed, so no pipeline is kept warm.
 *
 * THE FRAME. The mesh is a scene child, so its `position` is ABSOLUTE; the
 * box the shader tests is RENDER space (positionWorld / cameraPosition carry
 * the FP64 rebase) — `setBoxWorld` gets `mesh.position + scene.position`.
 *
 * Lever: `__lightVolume()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { createShadowedLightVolume, type ShadowedLightVolumeHandle } from 'enginex/render/fog/ShadowedLightVolume.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor } from './SkyModule.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { VOLUME_SIZE_M, volumeStepsFor, volumeDensityFor, volumeAmbientScale, stage5Stats } from '../lib/stage5_fx.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';

const FOLLOW_STEP_M = 8;
const AMBIENT_DAY = new THREE.Vector3(0.03, 0.035, 0.045);

export class AtlasVolumetricLightModule implements IModule {
  readonly name = 'VolumetricLight';
  private app!: App;
  private handle: ShadowedLightVolumeHandle | null = null;
  private anchorX = Number.NaN;
  private anchorZ = Number.NaN;
  private frame = 0;
  private lastTier = '';
  private readonly center = new THREE.Vector3();
  private readonly half = new THREE.Vector3(VOLUME_SIZE_M[0] / 2, VOLUME_SIZE_M[1] / 2, VOLUME_SIZE_M[2] / 2);
  private lastMs = 0;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasVolumetricLight', Phase.RenderSub, 10, () => this.tick()));
    (globalThis as unknown as { __lightVolume?: () => unknown }).__lightVolume = () => ({
      built: !!this.handle,
      flag: !!activePreset().volumetricLights,
      density: this.handle?.uniforms.density.value ?? 0,
      steps: this.handle?.material.steps ?? 0,
      ambient: this.handle ? [this.handle.uniforms.ambient.value.x, this.handle.uniforms.ambient.value.y, this.handle.uniforms.ambient.value.z] : null,
      box: this.handle ? { min: this.handle.uniforms.boxMin.value.toArray(), max: this.handle.uniforms.boxMax.value.toArray() } : null,
      anchor: [this.anchorX, this.anchorZ],
      inScene: !!this.handle && this.handle.mesh.parent === this.app.world.scene,
    });
  }

  private ensure(on: boolean): void {
    if (on && !this.handle) {
      const tier = activeTier();
      this.handle = createShadowedLightVolume({ size: VOLUME_SIZE_M, steps: volumeStepsFor(tier), density: 0.005, g: 0.55 });
      this.lastTier = tier;
      this.anchorX = Number.NaN; this.anchorZ = Number.NaN;
      this.app.world.scene.add(this.handle.mesh);
      stage5Stats.volumeBuilt = true;
    } else if (!on && this.handle) {
      deferDisposeTree(this.handle.mesh);
      this.handle = null;
      stage5Stats.volumeBuilt = false;
    }
  }

  private tick(): void {
    this.frame++;
    const p = activePreset();
    const want = !!p.volumetricLights && !atlas.spaceFlight && atlas.currentWorldId != null && !atlas.loadingGate;
    this.ensure(want);
    const h = this.handle;
    if (!h) return;
    // Follow the player in steps (absolute), the box floor just under the feet.
    const px = atlas.localX, pz = atlas.localZ;
    if (Number.isNaN(this.anchorX) || Math.hypot(px - this.anchorX, pz - this.anchorZ) > FOLLOW_STEP_M) {
      this.anchorX = Math.round(px / FOLLOW_STEP_M) * FOLLOW_STEP_M;
      this.anchorZ = Math.round(pz / FOLLOW_STEP_M) * FOLLOW_STEP_M;
    }
    h.mesh.position.set(this.anchorX, atlas.localY + this.half.y - 2.5, this.anchorZ);
    // The shader tests RENDER space: fold the rebase in.
    const sp = this.app.world.scene.position;
    this.center.set(h.mesh.position.x + sp.x, h.mesh.position.y + sp.y, h.mesh.position.z + sp.z);
    h.setBoxWorld(this.center, this.half);
    // Steps follow the tier live (an int uniform + the material's own count).
    const tier = activeTier();
    if (tier !== this.lastTier) {
      this.lastTier = tier;
      const steps = volumeStepsFor(tier);
      h.material.steps = steps;
      (h.uniforms.steps as { value: number }).value = steps;
      h.material.needsUpdate = true;
    }
    // Density from the sky's own fog + the weather, at ~10 Hz.
    if (this.frame % 6 === 0) {
      const fog = this.app.world.scene.fog as THREE.FogExp2 | null;
      const weather = this.app.getModule('Weather') as { getPrecip?: () => { kind: string; density: number }; getDust?: () => number; wind?: () => { x: number; z: number; strength: number } } | undefined;
      const kind = weather?.getPrecip?.().kind ?? 'clear';
      const dust = weather?.getDust?.() ?? 0;
      const density = volumeDensityFor(fog?.density ?? 0.002, kind, dust);
      h.uniforms.density.value = density;
      stage5Stats.volumeDensity = density;
      stage5Stats.volumeSteps = h.material.steps;
      const night = (uNightFactor as unknown as { value: number }).value ?? 0;
      const a = volumeAmbientScale(night);
      h.uniforms.ambient.value.set(AMBIENT_DAY.x * a, AMBIENT_DAY.y * a, AMBIENT_DAY.z * a);
      // The noise drifts with the wind.
      const w = weather?.wind?.();
      const now = performance.now();
      const dt = this.lastMs ? Math.min(0.5, (now - this.lastMs) / 1000) : 0;
      this.lastMs = now;
      if (w) { const wv = h.uniforms.wind.value; wv.x += (w.x * w.strength * 0.6 * dt) / 9; wv.z += (w.z * w.strength * 0.6 * dt) / 9; }
    }
  }

  dispose(): void { this.ensure(false); }
}
